/**
 * landingV3.home.js — el copy de la home del rediseño V3.
 *
 * Vive aparte de `landingV3.content.js` porque la home sola tiene más prosa que
 * el resto de la V3 junta, y con las once páginas dentro de un único fichero
 * acabaríamos con otro `landingV2.content.js` de 3.374 líneas donde encontrar
 * una frase es un `grep`. El módulo raíz lo importa y lo cuelga de `home`.
 *
 * Aquí NO hay marcado: ni JSX, ni iconos, ni clases. Todo lo que hay es texto
 * que alguien podría mandar a traducir sin entender React. Lo que el diseño
 * resuelve con un `<span>` de color en mitad de un titular se parte en trozos
 * (`tituloA`, `acento1`, …) por eso mismo.
 */
const es = {
  hero: {
    badge: 'Para negocios que quieren atraer más clientes por Google Maps',
    tituloA: 'Posiciónate como la ',
    acento1: 'opción Nº1',
    tituloB: ' en Google Maps y atrae ',
    acento2: 'más clientes',
    subtitulo:
      'La plataforma donde gestionas todas las ubicaciones de tus perfiles de Google: respuestas automatizadas con IA, análisis SEO de tu perfil y publicaciones programadas.',
    ctaPrimario: 'Probar 7 días gratis',
    ctaSecundario: 'Ver cómo funciona',

    // El mock animado de la derecha.
    busqueda: '“tu categoría” cerca de mí',
    tuNegocio: 'Tu negocio',
    // Los rivales del listado son de mentira y se numeran; poner nombres
    // inventados con pinta de real (“Sushi Kanda”) en la home invita a que
    // alguien los busque en Google.
    competidor: 'Competidor',
    kpiLlamadas: '+277% llamadas',
    kpiVisitas: '+345% visitas',
    mejorOpcion: 'Mejor opción',
    subiendo: '↑ subiendo',
    // Lo que anuncia un lector de pantalla en lugar de la animación.
    rankingResumen:
      'Ejemplo: tu negocio sube del cuarto al primer puesto en el listado de Google Maps.',
  },

  respaldo: {
    titulo: 'Respaldado por',
    alt: 'Lanzadera',
  },

  como: {
    titulo: 'Cómo funciona',
    subtitulo: 'En tres pasos sabes dónde estás y qué tienes que hacer para subir.',
    pasos: [
      {
        clave: 'conectar',
        titulo: 'Conectas tu ficha de Google',
        desc: 'En menos de 30 segundos, enlazas tu perfil de Google Business.',
      },
      {
        clave: 'mapa',
        titulo: 'El mapa SEO te dice tu posición',
        desc: 'Te decimos en qué posición sales en cada punto donde te buscan tus clientes.',
      },
      {
        clave: 'analisis',
        titulo: 'El análisis te dice cómo subir',
        desc: 'Una puntuación sobre 100 y las acciones ordenadas por importancia.',
      },
    ],
    // Textos sueltos de las tres tarjetas-maqueta.
    mockFicha: {
      negocio: 'Tu negocio',
      fuente: 'Google',
      resenas: 'Reseñas',
      respondidas: 'respondidas',
      puntuacion: 'Puntuación SEO',
      tuMedia: 'Tu media',
      objetivo: 'Objetivo',
      faltanA: 'Te faltan ',
      faltanResenas: '609 reseñas',
      faltanB: ' de 5★ para llegar a 4,8',
      conectada: 'Ficha conectada',
    },
    mockMapa: { titulo: 'Mapa SEO', posicionMedia: 'Posición media' },
    mockAnalisis: {
      nivel: 'Tu nivel',
      etiqueta: 'Bien posicionado',
      progreso: '+17 pts este mes',
      proxima: 'Próxima acción · mayor impacto',
      accion: 'Sube fotos recientes al perfil',
      puntos: '+7 pts al completar',
    },
  },

  mapa: {
    kicker: 'Mapa SEO local',
    titulo:
      'Comprueba en qué posición apareces en Google Maps según desde dónde te busquen',
    cuerpo:
      'No apareces en la misma posición en todas las zonas. Los clientes buscan tu tipo de negocio desde distintas ubicaciones y tu posición varía según la zona. Escaneamos tu posición en diferentes puntos y te mostramos tus competidores cercanos, comparando tu posición con la suya.',
    abrirGuia: '¿Por qué cambia mi posición?',
    listaTitulo: 'Negocios de tu zona',
    puntoAPunto: 'Punto por punto',
    tu: 'Tú',
    // Repetidos del hero a propósito: son la misma palabra pero en otra
    // sección, y al traducir no tiene por qué caer igual («Tu negocio» en un
    // listado y en una etiqueta de comparación admiten formas distintas).
    // Alcanzarlos desde `hero` ataría las dos secciones.
    tuNegocio: 'Tu negocio',
    competidor: 'Competidor',
    posicionMedia: 'Posición media ',
    // `{pct}` lo sustituye el componente. Se deja como marca y no como
    // concatenación para que al traducir se pueda mover de sitio.
    leGanas: 'Le ganas en {pct}%',
    teGana: 'Te gana en {pct}%',
    empatais: 'Empatáis',

    // Etiquetas de la leyenda de la vista DIFERENCIA. Las mismas palabras
    // que el panel (`gbusiness.geogrid.legendWin` y compañeras), para que
    // quien vea la landing y luego entre reconozca los colores.
    // El globo que sale del punto central cada pocos segundos. Señala cuál
    // de los veinticinco es tu local, que desde que no hay pin negro no se
    // distingue de los demás.
    globoCentro: '¡Este es tu local!',

    leyendaGanas: 'Le ganas',
    leyendaEmpate: 'Empate',
    leyendaPierdes: 'Te gana',
    leyendaSinDato: 'Sin datos',
    vistas: { mio: 'Mi negocio', rival: 'Competidor', diff: 'Diferencia' },
    resumenBase:
      'Cada punto es una zona desde la que buscan tus clientes, y el número es el puesto en el que sales desde ahí.',
    resumenDiff: 'Le ganas en {gano} puntos, te gana en {pierdo} y empatáis en {empate}.',

    guia: {
      paso1Titulo: 'Por qué tu posición cambia según dónde busquen',
      paso1Cuerpo:
        'Si alguien busca en Google desde la puerta de tu negocio, lo más probable es que salgas entre los primeros. Pero esa misma búsqueda, hecha un par de calles más allá, puede dar otro resultado: influye lo trabajada que esté tu ficha, cuántos competidores hay cerca y lo bien posicionados que estén ellos.',
      paso1Destacado: 'Esta herramienta mide exactamente eso: dónde te encuentran y dónde no.',
      paso1Pie:
        'Ejemplo real: en el centro sales el primero; a pocas calles, el octavo, el vigésimo… o no sales.',
      paso1Alt: 'Mapa SEO con las posiciones por zona',

      paso2Titulo: 'Qué significa cada punto',
      paso2Cuerpo:
        'Cada punto del mapa es una búsqueda hecha desde ese lugar exacto, y el número de dentro es la posición en la que aparece tu negocio.',
      paso2Nota1A: 'Del 1 al 3',
      paso2Nota1B:
        ' estás en el bloque que Google enseña sin que nadie tenga que desplegar más resultados. Ahí se decide la mayoría de las visitas.',
      paso2Nota2A: '«20+» y el gris no son lo mismo:',
      paso2Nota2B:
        ' con 20+ apareces, pero por debajo del puesto veinte. El gris significa que en esa búsqueda no apareces en absoluto.',

      leyenda: ['Posición 1-3', '4-7', '8-10', '11-15', '16-20', '20+', 'No apareces'],
      paso: 'Paso {n} de 2',
      siguiente: 'Siguiente →',
      entendido: 'Entendido',
      atras: '← Atrás',
      cerrar: 'Cerrar',
    },
  },

  seo: {
    kicker: 'Tu puntuación de SEO local',
    hoy: {
      titulo: 'Así está tu perfil hoy',
      cuerpo:
        'Nuestra plataforma analiza tu perfil y te da una puntuación del 0 al 100 según qué tan optimizado está para aparecer en búsquedas locales.',
      ejemplo: 'Ejemplo · nivel',
      // Uno de los CINCO niveles que existen de verdad. Decía «Camino a
      // posicionar», que no es ninguno: la escalera del producto es Empezando
      // → Visible online → Camino al podio → Bien posicionado → Líder local
      // (`gbusiness.healthScore.level.*`). Enseñar en la landing una etiqueta
      // que el panel nunca escribe es prometer una pantalla que no existe, y
      // además deja la traducción a merced de quien traduzca, en vez de
      // reutilizar la del producto.
      nivel: 'Camino al podio',
      pie: 'Cada nivel te acerca al bloque de los tres primeros.',
    },
    falta: {
      titulo: 'Sabes exactamente qué te falta',
      cuerpo:
        'Revisamos tu perfil en 6 áreas (reputación, actividad, relevancia de categoría, ficha NAP, presencia visual y keywords y servicios) y te damos las acciones ordenadas por impacto, para que sepas por dónde empezar sin adivinar ni contratar a nadie.',
      empieza: 'Empieza por aquí',
      accion1: 'Responde las reseñas que llevan días esperando',
      area1: 'Reputación · respuestas en menos de 24 h',
      accion2: 'Publica constantemente en tu ficha',
      area2: 'Actividad',
      accion3: 'Te faltan categorías adicionales en tu ficha',
      area3: 'Relevancia de categoría',
    },
    /*
     * ⚠️ NO PROMETER AQUÍ EL PLAN DE MEJORA.
     *
     * Sin registrarse el visitante ve su puntuación y el diagnóstico de la
     * ficha: número de reseñas, nota media, cuándo fue la última, datos y
     * fotos. Lo que NO ve es el plan de mejora —los temas de clientes—, que
     * sale borroso con un botón que lleva a crear cuenta (`AuditorLocked`).
     *
     * La versión anterior de este copy decía «te decimos qué hacer para
     * subirla, por orden de importancia» y remataba con «sin registrarte». Eran
     * las dos mitades de una promesa que el producto no cumple junta: se le
     * ofrecía justo lo que está detrás del muro, y el muro aparecía treinta
     * segundos después. Eso no es un matiz de redacción, es prometer algo y no
     * darlo.
     *
     * Lo gratis se nombra como gratis y lo de pago como de pago, antes de que
     * el visitante gaste su tiempo. Si al llegar al muro se siente engañado, la
     * cuenta que no abre es la nuestra.
     */
    auditor: {
      titulo: 'Cada una de tus fichas ya tiene su puntuación',
      cuerpo:
        'Escribe tu local y te decimos su puntuación y cómo está tu ficha hoy: reseñas, nota media, fotos y datos.',
      microcopy: 'Tu puntuación, gratis y sin registrarte · el plan de mejora, al crear la cuenta',
    },
  },

  funciones: {
    kicker: 'Así subes de posición',
    items: [
      {
        clave: 'respuestas',
        tag: 'Reseñas',
        titulo: 'Respuestas automatizadas con tono personalizado',
        desc: 'La IA contesta a cada reseña con tu estilo de comunicación. Las de 1 a 3 estrellas nunca salen sin tu visto bueno.',
      },
      {
        clave: 'antiguas',
        tag: 'Reseñas',
        titulo: 'Responde reseñas antiguas con un clic',
        desc: 'Responde a todas tus reseñas anteriores automáticamente, ahorrando tiempo.',
      },
      {
        clave: 'programar',
        tag: 'Publicaciones',
        titulo: 'Programa publicaciones para no perder constancia',
        desc: 'Dejas el mes entero listo de una sentada y se publica solo, semana a semana.',
      },
      {
        clave: 'perfil',
        tag: 'Perfil',
        titulo: 'Edita tu perfil y mira la tasa de conversión',
        desc: 'Cambias fotos, horarios y servicios sin entrar en Google, y consultas tus métricas para ver cuánta gente ve tu ficha y decide ir a tu local.',
      },
    ],
  },

  /*
   * ⚠️ CIFRAS SIN CONFIRMAR. El diseño dice «+10.000 negocios» y «+1.000.000
   * reseñas»; la landing V2 que hay hoy en producción dice «8.000 locales» y
   * «700.000 reseñas». Alguien tiene que decidir cuál es la verdad antes de que
   * esto sustituya a `/`: son afirmaciones públicas sobre el tamaño del negocio.
   */
  cifras: {
    titulo:
      'Miles de negocios en España confían en MyTapstar para subir posiciones en Google Maps',
    items: [
      { valor: '+10.000', etiqueta: 'negocios gestionan su ficha con MyTapstar' },
      { valor: '+1.000.000', etiqueta: 'reseñas gestionadas desde la plataforma' },
    ],
    logosTitulo: 'Algunas de las marcas que confían en MyTapstar para subir posiciones',
  },

  cierre: {
    tituloA: 'Ya lo has visto todo. ',
    acento: 'Pruébalo.',
    cta: 'Probar 7 días gratis',
    microcopy: 'Conectas tu ficha en dos minutos · sin compromiso · cancelas cuando quieras',
    enterpriseA: '¿Más de 30 locales? ',
    enterpriseCta: 'Contacta con ventas',
  },
};

/*
 * ── LAS CUATRO TRADUCCIONES ─────────────────────────────────────────────────
 *
 * Reglas que valen para los cuatro módulos de contenido de la V3 y que el test
 * `landingV3Content.test.js` comprueba una por una:
 *
 *  1. **Las claves y la forma no se tocan.** Mismas claves, arrays de la misma
 *     longitud, y `clave`/`to`/`src`/`ancho`/`alto` IDÉNTICOS al español: son
 *     claves de React, rutas y rutas de imagen, no texto.
 *  2. **Las marcas `{…}` se conservan.** `{pct}`, `{gano}`, `{pierdo}`,
 *     `{empate}`, `{n}`. Se pueden mover dentro de la frase; perder una deja
 *     un hueco en pantalla.
 *  3. **Los números se localizan.** `4,8` → `4.8` en inglés; `+10.000` →
 *     `+10,000` en inglés y `+10 000` en francés. Y en alemán y francés el
 *     porcentaje lleva espacio antes del signo (`+277 %`).
 *
 * EL TRATO, que es el de la V2 y no se cambia a mitad de sitio:
 * inglés británico, portugués de Portugal y formal (`o seu`), alemán **de tú**
 * (`du`), francés **de usted** (`vous`).
 *
 * LO QUE SE COPIA DEL PRODUCTO, no se traduce a mano: los nombres de los cinco
 * niveles de la puntuación salen de `gbusiness.healthScore.level.*` en
 * `translation.json`. Si la landing dice «On the way up» y el panel «Climbing»,
 * el visitante que entra cree que está mirando otra cosa.
 */
const en = {
  hero: {
    badge: 'For businesses that want to attract more customers from Google Maps',
    tituloA: 'Position yourself as the ',
    acento1: 'No. 1 choice',
    tituloB: ' on Google Maps and attract ',
    acento2: 'more customers',
    subtitulo:
      'The platform where you manage every location of your Google profiles: replies written by AI, an SEO analysis of your profile and scheduled posts.',
    ctaPrimario: 'Try 7 days free',
    ctaSecundario: 'See how it works',

    busqueda: '“your category” near me',
    tuNegocio: 'Your business',
    competidor: 'Competitor',
    kpiLlamadas: '+277% calls',
    kpiVisitas: '+345% visits',
    mejorOpcion: 'Top choice',
    subiendo: '↑ rising',
    rankingResumen:
      'Example: your business climbs from fourth to first place in the Google Maps listing.',
  },

  respaldo: {
    titulo: 'Backed by',
    alt: 'Lanzadera',
  },

  como: {
    titulo: 'How it works',
    subtitulo: 'Three steps to know where you stand and what to do to climb.',
    pasos: [
      {
        clave: 'conectar',
        titulo: 'You connect your Google listing',
        desc: 'In under 30 seconds, you link your Google Business profile.',
      },
      {
        clave: 'mapa',
        titulo: 'The SEO map tells you your position',
        desc: 'We tell you the position you come up in at every point where your customers search.',
      },
      {
        clave: 'analisis',
        titulo: 'The analysis tells you how to climb',
        desc: 'A score out of 100 and the actions ranked by importance.',
      },
    ],
    mockFicha: {
      negocio: 'Your business',
      fuente: 'Google',
      resenas: 'Reviews',
      respondidas: 'answered',
      puntuacion: 'SEO score',
      tuMedia: 'Your average',
      objetivo: 'Target',
      faltanA: 'You need ',
      faltanResenas: '609 reviews',
      faltanB: ' at 5★ to reach 4.8',
      conectada: 'Listing connected',
    },
    mockMapa: { titulo: 'SEO map', posicionMedia: 'Average position' },
    mockAnalisis: {
      nivel: 'Your level',
      etiqueta: 'Well positioned',
      progreso: '+17 pts this month',
      proxima: 'Next action · biggest impact',
      accion: 'Upload recent photos to your profile',
      puntos: '+7 pts once done',
    },
  },

  mapa: {
    kicker: 'Local SEO map',
    titulo:
      'Check what position you come up in on Google Maps depending on where people search from',
    cuerpo:
      'You do not come up in the same position everywhere. Customers search for your kind of business from different locations and your position varies from area to area. We scan your position at different points and show you the competitors nearby, comparing your position with theirs.',
    abrirGuia: 'Why does my position change?',
    listaTitulo: 'Businesses in your area',
    puntoAPunto: 'Point by point',
    tu: 'You',
    tuNegocio: 'Your business',
    competidor: 'Competitor',
    posicionMedia: 'Average position ',
    leGanas: 'You win at {pct}%',
    teGana: 'They win at {pct}%',
    empatais: 'You tie',

    globoCentro: 'This is your business!',

    leyendaGanas: 'You win',
    leyendaEmpate: 'Tie',
    leyendaPierdes: 'They win',
    leyendaSinDato: 'No data',
    vistas: { mio: 'My business', rival: 'Competitor', diff: 'Difference' },
    resumenBase:
      'Each point is an area your customers search from, and the number is the position you come up in from there.',
    resumenDiff: 'You win at {gano} points, they win at {pierdo} and you tie at {empate}.',

    guia: {
      paso1Titulo: 'Why your position changes depending on where people search',
      paso1Cuerpo:
        'If someone searches on Google from your front door, you will most likely come up among the first. But that same search, made a couple of streets away, can give a different result: it depends on how well your listing is filled in, how many competitors are nearby and how well positioned they are.',
      paso1Destacado:
        'This tool measures exactly that: where people find you and where they do not.',
      paso1Pie:
        'A real example: in the centre you come up first; a few streets away, eighth, twentieth… or not at all.',
      paso1Alt: 'SEO map with the positions by area',

      paso2Titulo: 'What each point means',
      paso2Cuerpo:
        'Each point on the map is a search made from that exact spot, and the number inside is the position your business appears in.',
      paso2Nota1A: 'From 1 to 3',
      paso2Nota1B:
        ' you are in the block Google shows without anyone having to open up more results. That is where most visits are decided.',
      paso2Nota2A: '“20+” and grey are not the same thing:',
      paso2Nota2B:
        ' with 20+ you do appear, but below position twenty. Grey means you do not appear at all in that search.',

      leyenda: ['Position 1-3', '4-7', '8-10', '11-15', '16-20', '20+', 'Not showing'],
      paso: 'Step {n} of 2',
      siguiente: 'Next →',
      entendido: 'Got it',
      atras: '← Back',
      cerrar: 'Close',
    },
  },

  seo: {
    kicker: 'Your local SEO score',
    hoy: {
      titulo: 'This is how your profile stands today',
      cuerpo:
        'Our platform analyses your profile and gives you a score from 0 to 100 based on how well optimised it is to show up in local searches.',
      ejemplo: 'Example · level',
      nivel: 'On the way up',
      pie: 'Every level brings you closer to the top-three block.',
    },
    falta: {
      titulo: 'You know exactly what you are missing',
      cuerpo:
        'We review your profile across 6 areas (reputation, activity, category relevance, NAP details, visual presence, and keywords and services) and give you the actions ranked by impact, so you know where to start without guessing or hiring anyone.',
      empieza: 'Start here',
      accion1: 'Answer the reviews that have been waiting for days',
      area1: 'Reputation · replies within 24 h',
      accion2: 'Post to your listing consistently',
      area2: 'Activity',
      accion3: 'Your listing is missing additional categories',
      area3: 'Category relevance',
    },
    auditor: {
      titulo: 'Every one of your listings already has a score',
      cuerpo:
        'Type in your business and we will tell you its score and how your listing stands today: reviews, average rating, photos and details.',
      microcopy:
        'Your score, free and without signing up · the improvement plan, when you create the account',
    },
  },

  funciones: {
    kicker: 'This is how you climb',
    items: [
      {
        clave: 'respuestas',
        tag: 'Reviews',
        titulo: 'Automated replies in a tone of your own',
        desc: 'AI answers every review in your communication style. The ones from 1 to 3 stars never go out without your approval.',
      },
      {
        clave: 'antiguas',
        tag: 'Reviews',
        titulo: 'Answer old reviews in one click',
        desc: 'Reply to all your previous reviews automatically, saving time.',
      },
      {
        clave: 'programar',
        tag: 'Posts',
        titulo: 'Schedule posts so you never lose consistency',
        desc: 'You set the whole month up in one sitting and it publishes on its own, week by week.',
      },
      {
        clave: 'perfil',
        tag: 'Profile',
        titulo: 'Edit your profile and watch the conversion rate',
        desc: 'You change photos, opening hours and services without going into Google, and check your metrics to see how many people see your listing and decide to visit you.',
      },
    ],
  },

  cifras: {
    titulo:
      'Thousands of businesses in Spain trust MyTapstar to climb positions on Google Maps',
    items: [
      { valor: '+10,000', etiqueta: 'businesses manage their listing with MyTapstar' },
      { valor: '+1,000,000', etiqueta: 'reviews managed from the platform' },
    ],
    logosTitulo: 'Some of the brands that trust MyTapstar to climb positions',
  },

  cierre: {
    tituloA: 'You have seen it all. ',
    acento: 'Try it.',
    cta: 'Try 7 days free',
    microcopy:
      'You connect your listing in two minutes · no commitment · cancel whenever you like',
    enterpriseA: 'More than 30 locations? ',
    enterpriseCta: 'Contact sales',
  },
};

const pt = {
  hero: {
    badge: 'Para negócios que querem atrair mais clientes pelo Google Maps',
    tituloA: 'Posicione-se como a ',
    acento1: 'opção Nº 1',
    tituloB: ' no Google Maps e atraia ',
    acento2: 'mais clientes',
    subtitulo:
      'A plataforma onde faz a gestão de todas as localizações dos seus perfis do Google: respostas automatizadas com IA, análise de SEO do seu perfil e publicações agendadas.',
    ctaPrimario: 'Testar 7 dias grátis',
    ctaSecundario: 'Ver como funciona',

    busqueda: '“a sua categoria” perto de mim',
    tuNegocio: 'O seu negócio',
    competidor: 'Concorrente',
    kpiLlamadas: '+277% chamadas',
    kpiVisitas: '+345% visitas',
    mejorOpcion: 'Melhor opção',
    subiendo: '↑ a subir',
    rankingResumen:
      'Exemplo: o seu negócio sobe do quarto para o primeiro lugar na lista do Google Maps.',
  },

  respaldo: {
    titulo: 'Apoiado por',
    alt: 'Lanzadera',
  },

  como: {
    titulo: 'Como funciona',
    subtitulo: 'Em três passos sabe onde está e o que tem de fazer para subir.',
    pasos: [
      {
        clave: 'conectar',
        titulo: 'Liga a sua ficha do Google',
        desc: 'Em menos de 30 segundos, liga o seu perfil do Google Business.',
      },
      {
        clave: 'mapa',
        titulo: 'O mapa de SEO diz-lhe a sua posição',
        desc: 'Dizemos-lhe em que posição aparece em cada ponto onde os seus clientes procuram.',
      },
      {
        clave: 'analisis',
        titulo: 'A análise diz-lhe como subir',
        desc: 'Uma pontuação de 0 a 100 e as ações ordenadas por importância.',
      },
    ],
    mockFicha: {
      negocio: 'O seu negócio',
      fuente: 'Google',
      resenas: 'Avaliações',
      respondidas: 'respondidas',
      puntuacion: 'Pontuação SEO',
      tuMedia: 'A sua média',
      objetivo: 'Objetivo',
      faltanA: 'Faltam-lhe ',
      faltanResenas: '609 avaliações',
      faltanB: ' de 5★ para chegar a 4,8',
      conectada: 'Ficha ligada',
    },
    mockMapa: { titulo: 'Mapa de SEO', posicionMedia: 'Posição média' },
    mockAnalisis: {
      nivel: 'O seu nível',
      etiqueta: 'Bem posicionado',
      progreso: '+17 pts este mês',
      proxima: 'Próxima ação · maior impacto',
      accion: 'Carregue fotografias recentes para o perfil',
      puntos: '+7 pts ao concluir',
    },
  },

  mapa: {
    kicker: 'Mapa de SEO local',
    titulo:
      'Veja em que posição aparece no Google Maps consoante o sítio de onde o procuram',
    cuerpo:
      'Não aparece na mesma posição em todas as zonas. Os clientes procuram o seu tipo de negócio a partir de localizações diferentes e a sua posição varia consoante a zona. Analisamos a sua posição em vários pontos e mostramos-lhe os concorrentes próximos, comparando a sua posição com a deles.',
    abrirGuia: 'Porque muda a minha posição?',
    listaTitulo: 'Negócios da sua zona',
    puntoAPunto: 'Ponto a ponto',
    tu: 'Você',
    tuNegocio: 'O seu negócio',
    competidor: 'Concorrente',
    posicionMedia: 'Posição média ',
    leGanas: 'Ganha-lhe em {pct}%',
    teGana: 'Perde-lhe em {pct}%',
    empatais: 'Empatam',

    globoCentro: 'Este é o seu local!',

    leyendaGanas: 'Ganha',
    leyendaEmpate: 'Empate',
    leyendaPierdes: 'Perde',
    leyendaSinDato: 'Sem dados',
    vistas: { mio: 'O meu negócio', rival: 'Concorrente', diff: 'Diferença' },
    resumenBase:
      'Cada ponto é uma zona a partir da qual os seus clientes procuram, e o número é a posição em que aparece a partir daí.',
    resumenDiff: 'Ganha-lhe em {gano} pontos, perde-lhe em {pierdo} e empatam em {empate}.',

    guia: {
      paso1Titulo: 'Porque é que a sua posição muda consoante o sítio de onde procuram',
      paso1Cuerpo:
        'Se alguém procurar no Google à porta do seu negócio, o mais provável é que apareça entre os primeiros. Mas essa mesma pesquisa, feita duas ruas mais à frente, pode dar outro resultado: influencia o quanto a sua ficha está trabalhada, quantos concorrentes há por perto e o quão bem posicionados estão.',
      paso1Destacado:
        'Esta ferramenta mede exatamente isso: onde o encontram e onde não.',
      paso1Pie:
        'Exemplo real: no centro aparece em primeiro; a poucas ruas, em oitavo, em vigésimo… ou não aparece.',
      paso1Alt: 'Mapa de SEO com as posições por zona',

      paso2Titulo: 'O que significa cada ponto',
      paso2Cuerpo:
        'Cada ponto do mapa é uma pesquisa feita a partir desse sítio exato, e o número lá dentro é a posição em que o seu negócio aparece.',
      paso2Nota1A: 'De 1 a 3',
      paso2Nota1B:
        ' está no bloco que o Google mostra sem que ninguém tenha de abrir mais resultados. É aí que se decide a maioria das visitas.',
      paso2Nota2A: '«20+» e o cinzento não são a mesma coisa:',
      paso2Nota2B:
        ' com 20+ aparece, mas abaixo da vigésima posição. O cinzento significa que nessa pesquisa não aparece de todo.',

      leyenda: ['Posição 1-3', '4-7', '8-10', '11-15', '16-20', '20+', 'Não aparece'],
      paso: 'Passo {n} de 2',
      siguiente: 'Seguinte →',
      entendido: 'Percebi',
      atras: '← Voltar',
      cerrar: 'Fechar',
    },
  },

  seo: {
    kicker: 'A sua pontuação de SEO local',
    hoy: {
      titulo: 'É assim que está o seu perfil hoje',
      cuerpo:
        'A nossa plataforma analisa o seu perfil e dá-lhe uma pontuação de 0 a 100 consoante o quão otimizado está para aparecer em pesquisas locais.',
      ejemplo: 'Exemplo · nível',
      nivel: 'Caminho do pódio',
      pie: 'Cada nível aproxima-o do bloco dos três primeiros.',
    },
    falta: {
      titulo: 'Sabe exatamente o que lhe falta',
      cuerpo:
        'Revemos o seu perfil em 6 áreas (reputação, atividade, relevância de categoria, dados NAP, presença visual e palavras-chave e serviços) e damos-lhe as ações ordenadas por impacto, para que saiba por onde começar sem adivinhar nem contratar ninguém.',
      empieza: 'Comece por aqui',
      accion1: 'Responda às avaliações que estão à espera há dias',
      area1: 'Reputação · respostas em menos de 24 h',
      accion2: 'Publique com constância na sua ficha',
      area2: 'Atividade',
      accion3: 'Faltam categorias adicionais na sua ficha',
      area3: 'Relevância de categoria',
    },
    auditor: {
      titulo: 'Cada uma das suas fichas já tem a sua pontuação',
      cuerpo:
        'Escreva o nome do seu local e dizemos-lhe a pontuação e como está a sua ficha hoje: avaliações, média, fotografias e dados.',
      microcopy:
        'A sua pontuação, grátis e sem registo · o plano de melhoria, ao criar a conta',
    },
  },

  funciones: {
    kicker: 'É assim que sobe de posição',
    items: [
      {
        clave: 'respuestas',
        tag: 'Avaliações',
        titulo: 'Respostas automatizadas com um tom personalizado',
        desc: 'A IA responde a cada avaliação com o seu estilo de comunicação. As de 1 a 3 estrelas nunca saem sem a sua aprovação.',
      },
      {
        clave: 'antiguas',
        tag: 'Avaliações',
        titulo: 'Responda a avaliações antigas com um clique',
        desc: 'Responda automaticamente a todas as suas avaliações anteriores, poupando tempo.',
      },
      {
        clave: 'programar',
        tag: 'Publicações',
        titulo: 'Agende publicações para não perder a constância',
        desc: 'Deixa o mês inteiro pronto de uma só vez e publica-se sozinho, semana a semana.',
      },
      {
        clave: 'perfil',
        tag: 'Perfil',
        titulo: 'Edite o seu perfil e veja a taxa de conversão',
        desc: 'Muda fotografias, horários e serviços sem entrar no Google, e consulta as suas métricas para ver quantas pessoas veem a sua ficha e decidem ir ao seu local.',
      },
    ],
  },

  cifras: {
    titulo:
      'Milhares de negócios em Espanha confiam no MyTapstar para subir posições no Google Maps',
    items: [
      { valor: '+10.000', etiqueta: 'negócios fazem a gestão da sua ficha com o MyTapstar' },
      { valor: '+1.000.000', etiqueta: 'avaliações geridas a partir da plataforma' },
    ],
    logosTitulo: 'Algumas das marcas que confiam no MyTapstar para subir posições',
  },

  cierre: {
    tituloA: 'Já viu tudo. ',
    acento: 'Experimente.',
    cta: 'Testar 7 dias grátis',
    microcopy:
      'Liga a sua ficha em dois minutos · sem compromisso · cancela quando quiser',
    enterpriseA: 'Mais de 30 locais? ',
    enterpriseCta: 'Fale com vendas',
  },
};

const de = {
  hero: {
    badge: 'Für Unternehmen, die über Google Maps mehr Kunden gewinnen wollen',
    tituloA: 'Positioniere dich als die ',
    acento1: 'Nr. 1',
    tituloB: ' bei Google Maps und gewinne ',
    acento2: 'mehr Kunden',
    subtitulo:
      'Die Plattform, auf der du alle Standorte deiner Google-Profile verwaltest: automatische Antworten mit KI, SEO-Analyse deines Profils und geplante Beiträge.',
    ctaPrimario: '7 Tage kostenlos testen',
    ctaSecundario: 'Ansehen, wie es funktioniert',

    busqueda: '„deine Kategorie“ in meiner Nähe',
    tuNegocio: 'Dein Unternehmen',
    competidor: 'Wettbewerber',
    kpiLlamadas: '+277 % Anrufe',
    kpiVisitas: '+345 % Besuche',
    mejorOpcion: 'Beste Wahl',
    subiendo: '↑ steigt',
    rankingResumen:
      'Beispiel: Dein Unternehmen steigt in der Google-Maps-Liste vom vierten auf den ersten Platz.',
  },

  respaldo: {
    titulo: 'Unterstützt von',
    alt: 'Lanzadera',
  },

  como: {
    titulo: 'So funktioniert es',
    subtitulo: 'In drei Schritten weißt du, wo du stehst und was du tun musst, um zu steigen.',
    pasos: [
      {
        clave: 'conectar',
        titulo: 'Du verbindest dein Google-Profil',
        desc: 'In weniger als 30 Sekunden verknüpfst du dein Google-Business-Profil.',
      },
      {
        clave: 'mapa',
        titulo: 'Die SEO-Karte zeigt dir deine Position',
        desc: 'Wir sagen dir, auf welcher Position du an jedem Punkt erscheinst, an dem deine Kunden suchen.',
      },
      {
        clave: 'analisis',
        titulo: 'Die Analyse zeigt dir, wie du steigst',
        desc: 'Eine Punktzahl von 0 bis 100 und die Maßnahmen nach Wichtigkeit sortiert.',
      },
    ],
    mockFicha: {
      negocio: 'Dein Unternehmen',
      fuente: 'Google',
      resenas: 'Bewertungen',
      respondidas: 'beantwortet',
      puntuacion: 'SEO-Punktzahl',
      tuMedia: 'Dein Durchschnitt',
      objetivo: 'Ziel',
      faltanA: 'Dir fehlen ',
      faltanResenas: '609 Bewertungen',
      faltanB: ' mit 5★, um 4,8 zu erreichen',
      conectada: 'Profil verbunden',
    },
    mockMapa: { titulo: 'SEO-Karte', posicionMedia: 'Durchschnittliche Position' },
    mockAnalisis: {
      nivel: 'Dein Level',
      etiqueta: 'Gut positioniert',
      progreso: '+17 Pkt. diesen Monat',
      proxima: 'Nächste Maßnahme · größte Wirkung',
      accion: 'Lade aktuelle Fotos in dein Profil',
      puntos: '+7 Pkt. nach Abschluss',
    },
  },

  mapa: {
    kicker: 'Lokale SEO-Karte',
    titulo:
      'Prüfe, auf welcher Position du bei Google Maps erscheinst — je nachdem, von wo aus gesucht wird',
    cuerpo:
      'Du erscheinst nicht überall auf derselben Position. Kunden suchen deine Art von Unternehmen von unterschiedlichen Orten aus, und deine Position ändert sich je nach Gegend. Wir scannen deine Position an verschiedenen Punkten und zeigen dir die Wettbewerber in der Nähe — mit deiner Position im Vergleich zu ihrer.',
    abrirGuia: 'Warum ändert sich meine Position?',
    listaTitulo: 'Unternehmen in deiner Gegend',
    puntoAPunto: 'Punkt für Punkt',
    tu: 'Du',
    tuNegocio: 'Dein Unternehmen',
    competidor: 'Wettbewerber',
    posicionMedia: 'Durchschnittliche Position ',
    leGanas: 'Du liegst bei {pct} % vorn',
    teGana: 'Er liegt bei {pct} % vorn',
    empatais: 'Gleichstand',

    globoCentro: 'Das ist dein Standort!',

    leyendaGanas: 'Du liegst vorn',
    leyendaEmpate: 'Gleichstand',
    leyendaPierdes: 'Er liegt vorn',
    leyendaSinDato: 'Keine Daten',
    vistas: { mio: 'Mein Unternehmen', rival: 'Wettbewerber', diff: 'Differenz' },
    resumenBase:
      'Jeder Punkt ist eine Gegend, aus der deine Kunden suchen, und die Zahl ist die Position, auf der du von dort aus erscheinst.',
    resumenDiff:
      'Du liegst an {gano} Punkten vorn, an {pierdo} zurück und an {empate} gleichauf.',

    guia: {
      paso1Titulo: 'Warum sich deine Position danach ändert, von wo aus gesucht wird',
      paso1Cuerpo:
        'Wenn jemand direkt vor deiner Tür bei Google sucht, erscheinst du sehr wahrscheinlich unter den Ersten. Dieselbe Suche, ein paar Straßen weiter, kann aber ein anderes Ergebnis liefern: Es hängt davon ab, wie gut dein Profil gepflegt ist, wie viele Wettbewerber in der Nähe sind und wie gut die positioniert sind.',
      paso1Destacado:
        'Genau das misst dieses Werkzeug: wo man dich findet und wo nicht.',
      paso1Pie:
        'Ein echtes Beispiel: im Zentrum bist du Erster; ein paar Straßen weiter Achter, Zwanzigster … oder gar nicht dabei.',
      paso1Alt: 'SEO-Karte mit den Positionen nach Gegend',

      paso2Titulo: 'Was jeder Punkt bedeutet',
      paso2Cuerpo:
        'Jeder Punkt auf der Karte ist eine Suche von genau diesem Ort aus, und die Zahl darin ist die Position, auf der dein Unternehmen erscheint.',
      paso2Nota1A: 'Von 1 bis 3',
      paso2Nota1B:
        ' bist du in dem Block, den Google zeigt, ohne dass jemand weitere Ergebnisse aufklappen muss. Dort entscheiden sich die meisten Besuche.',
      paso2Nota2A: '„20+“ und Grau sind nicht dasselbe:',
      paso2Nota2B:
        ' bei 20+ erscheinst du, aber unterhalb von Position zwanzig. Grau heißt, dass du bei dieser Suche überhaupt nicht erscheinst.',

      leyenda: ['Position 1-3', '4-7', '8-10', '11-15', '16-20', '20+', 'Nicht dabei'],
      paso: 'Schritt {n} von 2',
      siguiente: 'Weiter →',
      entendido: 'Verstanden',
      atras: '← Zurück',
      cerrar: 'Schließen',
    },
  },

  seo: {
    kicker: 'Deine lokale SEO-Punktzahl',
    hoy: {
      titulo: 'So steht dein Profil heute da',
      cuerpo:
        'Unsere Plattform analysiert dein Profil und gibt dir eine Punktzahl von 0 bis 100 — je nachdem, wie gut es für lokale Suchen optimiert ist.',
      ejemplo: 'Beispiel · Level',
      nivel: 'Auf dem Weg nach oben',
      pie: 'Jedes Level bringt dich näher an den Block der ersten drei.',
    },
    falta: {
      titulo: 'Du weißt genau, was dir fehlt',
      cuerpo:
        'Wir prüfen dein Profil in 6 Bereichen (Reputation, Aktivität, Kategorie-Relevanz, NAP-Daten, visuelle Präsenz sowie Keywords und Leistungen) und geben dir die Maßnahmen nach Wirkung sortiert, damit du weißt, wo du anfängst — ohne zu raten und ohne jemanden zu beauftragen.',
      empieza: 'Fang hier an',
      accion1: 'Beantworte die Bewertungen, die seit Tagen warten',
      area1: 'Reputation · Antworten in unter 24 Std.',
      accion2: 'Veröffentliche regelmäßig in deinem Profil',
      area2: 'Aktivität',
      accion3: 'In deinem Profil fehlen zusätzliche Kategorien',
      area3: 'Kategorie-Relevanz',
    },
    auditor: {
      titulo: 'Jedes deiner Profile hat schon seine Punktzahl',
      cuerpo:
        'Gib deinen Standort ein und wir sagen dir seine Punktzahl und wie dein Profil heute dasteht: Bewertungen, Durchschnitt, Fotos und Daten.',
      microcopy:
        'Deine Punktzahl, kostenlos und ohne Anmeldung · der Verbesserungsplan, sobald du das Konto anlegst',
    },
  },

  funciones: {
    kicker: 'So steigst du in der Position',
    items: [
      {
        clave: 'respuestas',
        tag: 'Bewertungen',
        titulo: 'Automatische Antworten im eigenen Ton',
        desc: 'Die KI antwortet auf jede Bewertung in deinem Kommunikationsstil. Die von 1 bis 3 Sternen gehen nie ohne deine Freigabe raus.',
      },
      {
        clave: 'antiguas',
        tag: 'Bewertungen',
        titulo: 'Beantworte alte Bewertungen mit einem Klick',
        desc: 'Antworte automatisch auf alle deine bisherigen Bewertungen und spare Zeit.',
      },
      {
        clave: 'programar',
        tag: 'Beiträge',
        titulo: 'Plane Beiträge, damit die Regelmäßigkeit nicht abreißt',
        desc: 'Du machst den ganzen Monat in einem Rutsch fertig und er veröffentlicht sich von allein, Woche für Woche.',
      },
      {
        clave: 'perfil',
        tag: 'Profil',
        titulo: 'Bearbeite dein Profil und sieh dir die Conversion Rate an',
        desc: 'Du änderst Fotos, Öffnungszeiten und Leistungen, ohne zu Google zu gehen, und schaust in deine Kennzahlen, um zu sehen, wie viele Leute dein Profil sehen und sich entscheiden, zu dir zu kommen.',
      },
    ],
  },

  cifras: {
    titulo:
      'Tausende Unternehmen in Spanien vertrauen MyTapstar, um bei Google Maps Positionen gutzumachen',
    items: [
      { valor: '+10.000', etiqueta: 'Unternehmen verwalten ihr Profil mit MyTapstar' },
      { valor: '+1.000.000', etiqueta: 'Bewertungen über die Plattform verwaltet' },
    ],
    logosTitulo: 'Einige der Marken, die MyTapstar vertrauen, um Positionen gutzumachen',
  },

  cierre: {
    tituloA: 'Du hast alles gesehen. ',
    acento: 'Probier es aus.',
    cta: '7 Tage kostenlos testen',
    microcopy:
      'Profil in zwei Minuten verbunden · unverbindlich · jederzeit kündbar',
    enterpriseA: 'Mehr als 30 Standorte? ',
    enterpriseCta: 'Vertrieb kontaktieren',
  },
};

const fr = {
  hero: {
    badge: 'Pour les entreprises qui veulent attirer plus de clients via Google Maps',
    tituloA: 'Positionnez-vous comme le ',
    acento1: 'choix nº 1',
    tituloB: ' sur Google Maps et attirez ',
    acento2: 'plus de clients',
    subtitulo:
      'La plateforme où vous gérez tous les établissements de vos profils Google : réponses automatisées par IA, analyse SEO de votre profil et publications programmées.',
    ctaPrimario: 'Essayer 7 jours gratuits',
    ctaSecundario: 'Voir comment ça marche',

    busqueda: '« votre catégorie » près de moi',
    tuNegocio: 'Votre établissement',
    competidor: 'Concurrent',
    kpiLlamadas: '+277 % d’appels',
    kpiVisitas: '+345 % de visites',
    mejorOpcion: 'Meilleur choix',
    subiendo: '↑ en hausse',
    rankingResumen:
      'Exemple : votre établissement passe de la quatrième à la première place dans la liste Google Maps.',
  },

  respaldo: {
    titulo: 'Soutenu par',
    alt: 'Lanzadera',
  },

  como: {
    titulo: 'Comment ça marche',
    subtitulo: 'En trois étapes, vous savez où vous en êtes et ce qu’il faut faire pour monter.',
    pasos: [
      {
        clave: 'conectar',
        titulo: 'Vous connectez votre fiche Google',
        desc: 'En moins de 30 secondes, vous reliez votre profil Google Business.',
      },
      {
        clave: 'mapa',
        titulo: 'La carte SEO vous donne votre position',
        desc: 'Nous vous disons à quelle position vous apparaissez à chaque endroit d’où vos clients cherchent.',
      },
      {
        clave: 'analisis',
        titulo: 'L’analyse vous dit comment monter',
        desc: 'Une note sur 100 et les actions classées par importance.',
      },
    ],
    mockFicha: {
      negocio: 'Votre établissement',
      fuente: 'Google',
      resenas: 'Avis',
      respondidas: 'traités',
      puntuacion: 'Note SEO',
      tuMedia: 'Votre moyenne',
      objetivo: 'Objectif',
      faltanA: 'Il vous manque ',
      faltanResenas: '609 avis',
      faltanB: ' à 5★ pour atteindre 4,8',
      conectada: 'Fiche connectée',
    },
    mockMapa: { titulo: 'Carte SEO', posicionMedia: 'Position moyenne' },
    mockAnalisis: {
      nivel: 'Votre niveau',
      etiqueta: 'Bien positionné',
      progreso: '+17 pts ce mois-ci',
      proxima: 'Prochaine action · impact le plus fort',
      accion: 'Ajoutez des photos récentes à votre profil',
      puntos: '+7 pts une fois faite',
    },
  },

  mapa: {
    kicker: 'Carte SEO locale',
    titulo:
      'Vérifiez à quelle position vous apparaissez sur Google Maps selon l’endroit d’où l’on vous cherche',
    cuerpo:
      'Vous n’apparaissez pas à la même position partout. Les clients cherchent votre type d’établissement depuis des endroits différents et votre position varie selon la zone. Nous analysons votre position en plusieurs points et vous montrons vos concurrents proches, en comparant votre position à la leur.',
    abrirGuia: 'Pourquoi ma position change-t-elle ?',
    listaTitulo: 'Établissements de votre zone',
    puntoAPunto: 'Point par point',
    tu: 'Vous',
    tuNegocio: 'Votre établissement',
    competidor: 'Concurrent',
    posicionMedia: 'Position moyenne ',
    leGanas: 'Vous le devancez sur {pct} %',
    teGana: 'Il vous devance sur {pct} %',
    empatais: 'Égalité',

    globoCentro: 'C’est votre établissement !',

    leyendaGanas: 'Vous devancez',
    leyendaEmpate: 'Égalité',
    leyendaPierdes: 'Il vous devance',
    leyendaSinDato: 'Aucune donnée',
    vistas: { mio: 'Mon établissement', rival: 'Concurrent', diff: 'Différence' },
    resumenBase:
      'Chaque point est une zone d’où vos clients cherchent, et le nombre est la position à laquelle vous apparaissez depuis là.',
    resumenDiff:
      'Vous le devancez sur {gano} points, il vous devance sur {pierdo} et vous êtes à égalité sur {empate}.',

    guia: {
      paso1Titulo: 'Pourquoi votre position change selon l’endroit d’où l’on cherche',
      paso1Cuerpo:
        'Si quelqu’un cherche sur Google depuis votre porte, vous apparaîtrez très probablement parmi les premiers. Mais cette même recherche, faite deux rues plus loin, peut donner un autre résultat : cela dépend du soin apporté à votre fiche, du nombre de concurrents à proximité et de leur propre positionnement.',
      paso1Destacado:
        'Cet outil mesure exactement cela : où l’on vous trouve et où l’on ne vous trouve pas.',
      paso1Pie:
        'Exemple réel : au centre vous êtes premier ; à quelques rues, huitième, vingtième… ou absent.',
      paso1Alt: 'Carte SEO avec les positions par zone',

      paso2Titulo: 'Ce que signifie chaque point',
      paso2Cuerpo:
        'Chaque point de la carte est une recherche faite depuis cet endroit précis, et le nombre à l’intérieur est la position à laquelle votre établissement apparaît.',
      paso2Nota1A: 'De 1 à 3',
      paso2Nota1B:
        ' vous êtes dans le bloc que Google affiche sans que personne ait à dérouler d’autres résultats. C’est là que se joue la majorité des visites.',
      paso2Nota2A: '« 20+ » et le gris ne sont pas la même chose :',
      paso2Nota2B:
        ' avec 20+ vous apparaissez, mais au-delà de la vingtième position. Le gris signifie que sur cette recherche vous n’apparaissez pas du tout.',

      leyenda: ['Position 1-3', '4-7', '8-10', '11-15', '16-20', '20+', 'Absent'],
      paso: 'Étape {n} sur 2',
      siguiente: 'Suivant →',
      entendido: 'Compris',
      atras: '← Retour',
      cerrar: 'Fermer',
    },
  },

  seo: {
    kicker: 'Votre note de SEO local',
    hoy: {
      titulo: 'Voici où en est votre profil aujourd’hui',
      cuerpo:
        'Notre plateforme analyse votre profil et vous donne une note de 0 à 100 selon son niveau d’optimisation pour apparaître dans les recherches locales.',
      ejemplo: 'Exemple · niveau',
      nivel: 'En route vers le podium',
      pie: 'Chaque niveau vous rapproche du bloc des trois premiers.',
    },
    falta: {
      titulo: 'Vous savez exactement ce qu’il vous manque',
      cuerpo:
        'Nous passons votre profil en revue sur 6 domaines (réputation, activité, pertinence de catégorie, informations NAP, présence visuelle, et mots-clés et services) et nous vous donnons les actions classées par impact, pour savoir par où commencer sans deviner ni recruter personne.',
      empieza: 'Commencez par ici',
      accion1: 'Répondez aux avis qui attendent depuis des jours',
      area1: 'Réputation · réponses en moins de 24 h',
      accion2: 'Publiez régulièrement sur votre fiche',
      area2: 'Activité',
      accion3: 'Il manque des catégories supplémentaires sur votre fiche',
      area3: 'Pertinence de catégorie',
    },
    auditor: {
      titulo: 'Chacune de vos fiches a déjà sa note',
      cuerpo:
        'Saisissez votre établissement et nous vous donnons sa note et l’état de votre fiche aujourd’hui : avis, note moyenne, photos et informations.',
      microcopy:
        'Votre note, gratuite et sans inscription · le plan d’amélioration, à la création du compte',
    },
  },

  funciones: {
    kicker: 'Voici comment vous montez',
    items: [
      {
        clave: 'respuestas',
        tag: 'Avis',
        titulo: 'Réponses automatisées avec un ton personnalisé',
        desc: 'L’IA répond à chaque avis dans votre style de communication. Ceux de 1 à 3 étoiles ne partent jamais sans votre validation.',
      },
      {
        clave: 'antiguas',
        tag: 'Avis',
        titulo: 'Répondez aux anciens avis en un clic',
        desc: 'Répondez automatiquement à tous vos avis passés et gagnez du temps.',
      },
      {
        clave: 'programar',
        tag: 'Publications',
        titulo: 'Programmez vos publications pour ne pas perdre le rythme',
        desc: 'Vous préparez le mois entier d’une traite et tout se publie tout seul, semaine après semaine.',
      },
      {
        clave: 'perfil',
        tag: 'Profil',
        titulo: 'Modifiez votre profil et suivez le taux de conversion',
        desc: 'Vous changez photos, horaires et services sans passer par Google, et vous consultez vos statistiques pour voir combien de personnes voient votre fiche et décident de venir.',
      },
    ],
  },

  cifras: {
    titulo:
      'Des milliers d’entreprises en Espagne font confiance à MyTapstar pour gagner des positions sur Google Maps',
    items: [
      { valor: '+10 000', etiqueta: 'entreprises gèrent leur fiche avec MyTapstar' },
      { valor: '+1 000 000', etiqueta: 'avis gérés depuis la plateforme' },
    ],
    logosTitulo: 'Quelques-unes des marques qui font confiance à MyTapstar pour gagner des positions',
  },

  cierre: {
    tituloA: 'Vous avez tout vu. ',
    acento: 'Essayez.',
    cta: 'Essayer 7 jours gratuits',
    microcopy:
      'Fiche connectée en deux minutes · sans engagement · annulez quand vous voulez',
    enterpriseA: 'Plus de 30 établissements ? ',
    enterpriseCta: 'Contacter les ventes',
  },
};

export const homeV3 = { es, en, pt, de, fr };
